import { Form, Formik } from "formik";
import * as Yup from "yup";
import React, { useEffect, useState } from "react";
import Select from "./shared/Select";
import { Button, Col, Row, Alert } from "react-bootstrap";
import TextInput from "./shared/TextInput";
import VerifyInvitationModal from "./modals/VerifyInvitationModal";
import { Invite } from "../models/invite.model";
import SuccessInvitationModal from "./modals/SuccessInvitationModal";
import { USER_AUTH_TYPE } from "../enums/user-auth-type.enum";
import { ProgramType } from "../models/program-type.model";
import { BASE_REWARD_SETTINGS } from "../constants/base-reward-settings.constant";
import { BASELINE_REWARD_SETTINGS_ENUM } from "../enums/baseline-reward-settings.enum";
import SuccessRegistrationModal from "./modals/SuccessRegistrationModal";
import TermsAndConditionsModal from "./modals/TermsAndConditionsModal";
import { Program, UserOwner } from "../store/user-owner/user-owner.model";

import styles from "../scss/InviteForm.module.scss";

const formSchema = Yup.object().shape({
  ownerFirstName: Yup.string().required("First name is required"),
  ownerLastName: Yup.string().required("Last name is required"),
  ownerEmail: Yup.string()
    .required("Email is required")
    .email("Invalid email address"),
  // baselineRewardSetting: Yup.string().required(
  //   "Baseline rewards settings is required"
  // ),
});

const initialValues = {
  ownerFirstName: "",
  ownerLastName: "",
  ownerEmail: "",
  ownerID: "",
  baselineRewardSetting: "",
};

type InviteFormProps = {
  userAuthType: USER_AUTH_TYPE;
  programType: ProgramType;
  owner: UserOwner | undefined;
  hasCompletedInvitation: boolean;
  hasCompletedRegistration: boolean;
  isProgramEnrolled: boolean;
};

export default function InviteForm({
  userAuthType,
  programType,
  owner,
  hasCompletedInvitation,
  hasCompletedRegistration,
  isProgramEnrolled,
}: InviteFormProps) {
  const [formValues, setFormValues] = useState<Partial<Invite>>(initialValues);
  const [showVerifyInvitation, setShowVerifyInvitation] = useState(false);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [showSuccessInvitation, setShowSuccessInvitation] = useState(false);
  const [showSuccessRegistration, setShowSuccessRegistration] = useState(false);
  const [invite, setInvite] = useState<Partial<Invite> | null>(null);
  const [inviteLink, setInviteLink] = useState("");
  const [program, setProgram] = useState<Program | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const confirmSubmitInvitation = (invitationLink: string) => {
    setInviteLink(invitationLink);
    setShowVerifyInvitation(false);
    setShowSuccessInvitation(true);
    setIsSubmitting(false);
  };

  const confirmTermsAndConditions = async () => {
    setShowTermsAndConditions(false);
    setShowSuccessRegistration(true);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE) {
      const program = owner?.programs.find(
        (program) => program.ownerEmail && program.ownerEmail.trim() !== ''
      );
      setProgram(program);

      setFormValues({
        ownerFirstName: program?.ownerFirstName || "",
        ownerLastName: program?.ownerLastName || "",
        ownerEmail: program?.ownerEmail || "",
        baselineRewardSetting: program?.baselineRewardSetting || "LOCATION",
      });
    } else {
      setFormValues({
        ownerFirstName: owner?.ownerFirstName || "",
        ownerLastName: owner?.ownerLastName || "",
        ownerEmail: owner?.ownerEmail || "",
        ownerID: owner?.ownerID || "",
        baselineRewardSetting: owner?.baseLineRewardSetting,
      });
    }
  }, [owner, userAuthType]);

  // Determine if the button should be disabled
  const isSubmitButtonDisabled = () => {
    // console.log('---')
    // console.log('- isSubmitButtonDisabled')
    // console.log('- hasCompletedRegistration => ', hasCompletedRegistration)
    // console.log('- hasCompletedRegistration => ', hasCompletedRegistration)
    // console.log('- isProgramEnrolled => ', isProgramEnrolled)
    // console.log('- isSubmitting => ', isSubmitting)
    // console.log('---')
    if (isSubmitting) return true;
    if (hasCompletedRegistration) return true;
    if (userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE && isProgramEnrolled)
      return true;
    if (!owner || owner?.labLocations?.length === 0) return true;
    return false;
  };

  return (
    <>
      {hasCompletedInvitation &&
        userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE &&
        !isProgramEnrolled && (
          <div className="flex flex-col">
            <Alert className="text-center mx-auto" variant="info">
              Invitation is currently pending. You can still make changes to
              this invitation, it will send another invitation email to the
              user.
            </Alert>
          </div>
        )}
      {(hasCompletedRegistration || isProgramEnrolled) && (
        <div className="flex flex-col">
          <Alert className="text-center mx-auto" variant="warning">
            <>
              <h2 className="font-bold text-lg">Success!</h2>
              You've successfully registered and enrolled for the new{" "}
              {programType === "PRP"
                ? "Partner Rewards Hub"
                : "PracticeBuilder"}{" "}
              experience. We are completing your profile setup. We will notify
              you once it's ready for you to login.
            </>
          </Alert>
        </div>
      )}
      {userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE && (
        <p>
          Please ensure you send the invitation to the owner or primary
          decision-maker at the practice.
        </p>
      )}
      <br />
      <Formik
        initialValues={formValues}
        enableReinitialize={true}
        validationSchema={formSchema}
        onSubmit={async (values, { resetForm }) => {
          setIsSubmitting(true);
          setInvite({
            ...values,
            ownerID: owner?.ownerID,
            invitationKey: owner?.invitationKey
          });
          if (userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE) {
            setShowVerifyInvitation(true);
          } else {
            setShowTermsAndConditions(true);
          }
        }}
      >
        {({
          errors,
          touched,
          values,
          setFieldValue,
          handleChange,
          handleBlur,
          isValid,
        }) => {
          return (
            <Form>
              <Row>
                <Col md={6}>
                  <TextInput
                    disabled={
                      hasCompletedRegistration ||
                      isProgramEnrolled ||
                      !owner ||
                      owner?.labLocations?.length === 0
                    }
                    required
                    value={values?.ownerFirstName}
                    name="ownerFirstName"
                    handleChange={handleChange}
                    type="text"
                    label={
                      userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE
                        ? "ECP First Name (Add Dr. To ECP name if applicable)"
                        : "ECP First Name"
                    }
                    className={`${
                      errors.ownerFirstName && touched.ownerFirstName
                        ? "input-error"
                        : ""
                    }`}
                  />
                </Col>
                <Col md={6}>
                  <TextInput
                    disabled={
                      hasCompletedRegistration ||
                      isProgramEnrolled ||
                      !owner ||
                      owner?.labLocations?.length === 0
                    }
                    required
                    value={values?.ownerLastName}
                    name="ownerLastName"
                    handleChange={handleChange}
                    type="text"
                    label={
                      userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE
                        ? "ECP Last Name"
                        : "ECP Last Name"
                    }
                    className={`${
                      errors.ownerLastName && touched.ownerLastName
                        ? "input-error"
                        : ""
                    }`}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <TextInput
                    disabled={
                      userAuthType === USER_AUTH_TYPE.COMARCH_CUSTOMER ||
                      hasCompletedRegistration ||
                      isProgramEnrolled ||
                      !owner ||
                      owner?.labLocations?.length === 0
                    }
                    required
                    value={values?.ownerEmail}
                    name="ownerEmail"
                    handleChange={handleChange}
                    type="text"
                    label={
                      userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE
                        ? "ECP Primary Email"
                        : "ECP Primary Email"
                    }
                    className={`${
                      errors.ownerEmail && touched.ownerEmail
                        ? "input-error"
                        : ""
                    }`}
                  />
                </Col>
                {/*{userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE && (*/}
                <Col md={6}>
                  <Select
                    required
                    disabled
                    options={BASE_REWARD_SETTINGS}
                    value={values?.baselineRewardSetting || ""}
                    name="baselineRewardSetting"
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    label="Baseline Reward Setting"
                    className={`${
                      errors.baselineRewardSetting &&
                      touched.baselineRewardSetting
                        ? "input-error"
                        : ""
                    }`}
                  />
                </Col>
                {/*)}*/}
              </Row>
              <Row>
                <Col>
                  <br />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  {userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE && (
                    <>
                      {/*<p>*/}
                      {/*  The baseline in the form below determines how program*/}
                      {/*  goals are applied. Please select the Baseline Reward*/}
                      {/*  Settings that are appropriate for your practice.*/}
                      {/*</p>*/}
                      {/*<ul>*/}
                      {/*  <li>*/}
                      {/*    <strong>*/}
                      {/*      Location-Level Baseline [Default selection]: Goals*/}
                      {/*      are set per location (e.g., $1,000 for each*/}
                      {/*      location)*/}
                      {/*    </strong>*/}
                      {/*  </li>*/}
                      {/*  <li>*/}
                      {/*    <strong>Practice-Level Baseline: </strong> Goals are*/}
                      {/*    combined across all locations (e.g., $10,000 across*/}
                      {/*    the full practice)*/}
                      {/*  </li>*/}
                      {/*</ul>*/}
                      {/*<br />*/}
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                        <div className="flex items-start gap-2">
                          <span className="text-amber-600 text-sm">ⓘ</span>
                          <p className="text-sm text-amber-800">
                            <strong>Note:</strong>
                            If ECP needs to update their name, email, you can modify the form details{" "}
                            <strong>
                              until the ECP has accepted the Terms and
                              Conditions.{" "}
                            </strong>
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                  {userAuthType != USER_AUTH_TYPE.COMARCH_EMPLOYEE && <></>}
                </Col>
              </Row>
              <div className="flex justify-end mt-4">
                {userAuthType === USER_AUTH_TYPE.COMARCH_EMPLOYEE ? (
                  <Button
                    disabled={isSubmitButtonDisabled() || !isValid}
                    variant="primary"
                    type="submit"
                  >
                    {isSubmitting
                      ? "Processing..."
                      : hasCompletedInvitation
                      ? "Update Invitation"
                      : "Submit Invitation"}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitButtonDisabled()}
                  >
                    {isSubmitting
                      ? "Processing..."
                      : "Go to Terms and Conditions"}
                  </Button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
      <VerifyInvitationModal
        isUpdateInvitation={hasCompletedInvitation}
        programType={programType}
        confirmSubmitInvitation={confirmSubmitInvitation}
        invite={invite}
        show={showVerifyInvitation}
        handleClose={() => {
          setShowVerifyInvitation(false);
          setIsSubmitting(false);
        }}
        userAuthType={userAuthType}
        inviteEmailLink={program?.inviteEmailLink || ""}
      />
      <SuccessInvitationModal
        isUpdateInvitation={!!program}
        inviteLink={inviteLink}
        show={showSuccessInvitation}
        handleClose={() => setShowSuccessInvitation(false)}
        program={
          programType === "PRP"
            ? "Partner Rewards Hub"
            : "PracticeBuilder"
        }
      />
      <TermsAndConditionsModal
        confirmTermsAndConditions={confirmTermsAndConditions}
        invite={invite}
        show={showTermsAndConditions}
        handleClose={() => {
          setShowTermsAndConditions(false);
          setIsSubmitting(false);
        }}
        programType={programType}
      />
      <SuccessRegistrationModal
        program={
          programType === "PRP"
            ? "Partner Rewards Hub"
            : "PracticeBuilder"
        }
        show={showSuccessRegistration}
        handleClose={() => setShowSuccessRegistration(false)}
      />
    </>
  );
}
